import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  EMPTY,
  from,
  of,
  throwError,
  defer,
  map,
  mergeMap,
  switchMap,
  tap,
  catchError,
  finalize,
  reduce,
  retry,
  timer,
} from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UploadProgress {
  state: 'hashing' | 'uploading' | 'completing' | 'done' | 'error' | 'paused';
  fileName: string;
  totalBytes: number;
  uploadedBytes: number;
  percentage: number;
  error?: string;
  uploadId?: string;
}

interface InitiateUploadResponse {
  uploadId: string;
  bucket: string;
  key: string;
  partSize: number;
  parts: { partNumber: number; presignedUrl: string }[];
}

interface CompletedPart {
  partNumber: number;
  eTag: string;
}

interface PersistedUploadState {
  uploadId: string;
  bucket: string;
  key: string;
  shootAssetId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  sha256: string;
  parts: { partNumber: number; presignedUrl: string }[];
  partSize: number;
  completedParts: CompletedPart[];
  additionalAssetKey?: string;
}

interface ServiceState {
  readonly paused: boolean;
  readonly cancelled: boolean;
  readonly activeXhrs: ReadonlyArray<XMLHttpRequest>;
  readonly currentUploadId: string | null;
}

const INITIAL_STATE: ServiceState = {
  paused: false,
  cancelled: false,
  activeXhrs: [],
  currentUploadId: null,
};

@Injectable({ providedIn: 'root' })
export class PostAssetUploadService {
  private readonly uploadProgress$ = new BehaviorSubject<UploadProgress | null>(null);
  public readonly progress$ = this.uploadProgress$.asObservable();
  public readonly isUploading$ = this.uploadProgress$.pipe(
    map((p) => p !== null && p.state !== 'done' && p.state !== 'error'),
  );

  private readonly state$ = new BehaviorSubject<ServiceState>(INITIAL_STATE);
  private activeSubscription: Subscription | null = null;
  private activeFile: File | null = null;

  private readonly beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
    const progress = this.uploadProgress$.value;
    if (progress && progress.state !== 'done' && progress.state !== 'error') {
      event.preventDefault();
    }
  };

  private get paused(): boolean {
    return this.state$.value.paused;
  }

  private get cancelled(): boolean {
    return this.state$.value.cancelled;
  }

  private get currentUploadId(): string | null {
    return this.state$.value.currentUploadId;
  }

  constructor(private http: HttpClient) {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  startUpload(file: File, shootAssetId: string, additionalAssetKey?: string): void {
    this.activeSubscription?.unsubscribe();
    this.activeFile = file;
    this.activeSubscription = this.uploadPostAsset(file, shootAssetId, additionalAssetKey).subscribe();
  }

  startResume(): void {
    if (!this.activeFile) return;
    this.activeSubscription?.unsubscribe();
    this.activeSubscription = this.resumeUpload(this.activeFile).subscribe();
  }

  uploadPostAsset(
    file: File,
    shootAssetId: string,
    additionalAssetKey?: string,
  ): Observable<UploadProgress> {
    this.updateState({ paused: false, cancelled: false, activeXhrs: [], currentUploadId: null });

    this.emitProgress({
      state: 'hashing',
      fileName: file.name,
      totalBytes: file.size,
      uploadedBytes: 0,
      percentage: 0,
    });

    return from(this.computeSha256(file)).pipe(
      switchMap((sha256) => {
        if (this.cancelled) return EMPTY;

        return this.initiateUpload({
          target: { shootAssetId },
          file: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || 'application/octet-stream',
            sha256,
          },
          additionalAssetKey,
        }).pipe(
          switchMap((response) => {
            this.updateState({ currentUploadId: response.uploadId });

            this.persistState({
              uploadId: response.uploadId,
              bucket: response.bucket,
              key: response.key,
              shootAssetId,
              fileName: file.name,
              fileSize: file.size,
              contentType: file.type || 'application/octet-stream',
              sha256,
              parts: response.parts,
              partSize: response.partSize,
              completedParts: [],
              additionalAssetKey,
            });

            return this.uploadPartsObservable(file, response).pipe(
              switchMap((completedParts) => {
                if (this.cancelled) return EMPTY;

                if (this.paused) {
                  const pausedProgress: UploadProgress = {
                    state: 'paused',
                    fileName: file.name,
                    totalBytes: file.size,
                    uploadedBytes: this.calculateUploadedBytes(completedParts, response.partSize, file.size),
                    percentage: this.calculatePercentage(completedParts.length, response.parts.length),
                    uploadId: response.uploadId,
                  };
                  this.emitProgress(pausedProgress);
                  return of(pausedProgress);
                }

                const completingProgress: UploadProgress = {
                  state: 'completing',
                  fileName: file.name,
                  totalBytes: file.size,
                  uploadedBytes: file.size,
                  percentage: 100,
                  uploadId: response.uploadId,
                };
                this.emitProgress(completingProgress);

                return this.completeUpload(response.uploadId, completedParts).pipe(
                  tap(() => {
                    this.removePersistedState(response.uploadId);
                    this.updateState({ currentUploadId: null });
                  }),
                  switchMap(() => {
                    const doneProgress: UploadProgress = {
                      state: 'done',
                      fileName: file.name,
                      totalBytes: file.size,
                      uploadedBytes: file.size,
                      percentage: 100,
                      uploadId: response.uploadId,
                    };
                    this.emitProgress(doneProgress);
                    return of(doneProgress);
                  }),
                );
              }),
            );
          }),
        );
      }),
      catchError((error: any) => {
        if (this.cancelled) return EMPTY;

        const persisted = this.currentUploadId ? this.loadPersistedState(this.currentUploadId) : null;
        const pausedProgress: UploadProgress = {
          state: 'paused',
          fileName: file.name,
          totalBytes: file.size,
          uploadedBytes: persisted
            ? this.calculateUploadedBytes(persisted.completedParts, persisted.partSize, file.size)
            : 0,
          percentage: this.calculatePercentage(persisted?.completedParts?.length ?? 0, persisted?.parts?.length ?? 0),
          uploadId: this.currentUploadId ?? undefined,
          error: this.paused ? undefined : error?.message || 'Upload failed',
        };
        this.emitProgress(pausedProgress);
        return of(pausedProgress);
      }),
    );
  }

  pauseUpload(): void {
    this.updateState({ paused: true });
    this.abortActiveXhrs();
  }

  resumeUpload(file: File): Observable<UploadProgress> {
    if (!this.currentUploadId) return EMPTY;

    const state = this.loadPersistedState(this.currentUploadId);
    if (!state) return EMPTY;

    this.updateState({ paused: false, cancelled: false, activeXhrs: [] });

    const remainingParts = state.parts.filter(
      (p) => !state.completedParts.some((cp) => cp.partNumber === p.partNumber),
    );

    const response: InitiateUploadResponse = {
      uploadId: state.uploadId,
      bucket: state.bucket,
      key: state.key,
      partSize: state.partSize,
      parts: remainingParts,
    };

    this.emitProgress({
      state: 'uploading',
      fileName: state.fileName,
      totalBytes: state.fileSize,
      uploadedBytes: this.calculateUploadedBytes(
        state.completedParts,
        state.partSize,
        state.fileSize,
      ),
      percentage: this.calculatePercentage(state.completedParts.length, state.parts.length),
      uploadId: state.uploadId,
    });

    const initialUploadedBytes = this.calculateUploadedBytes(state.completedParts, state.partSize, state.fileSize);
    return this.uploadPartsObservable(file, response, initialUploadedBytes).pipe(
      switchMap((newlyCompleted) => {
        if (this.cancelled || this.paused) return EMPTY;

        const allCompleted = [...state.completedParts, ...newlyCompleted];

        this.emitProgress({
          state: 'completing',
          fileName: state.fileName,
          totalBytes: state.fileSize,
          uploadedBytes: state.fileSize,
          percentage: 100,
          uploadId: state.uploadId,
        });

        return this.completeUpload(state.uploadId, allCompleted).pipe(
          tap(() => {
            this.removePersistedState(state.uploadId);
            this.updateState({ currentUploadId: null });
          }),
          switchMap(() => {
            const doneProgress: UploadProgress = {
              state: 'done',
              fileName: state.fileName,
              totalBytes: state.fileSize,
              uploadedBytes: state.fileSize,
              percentage: 100,
              uploadId: state.uploadId,
            };
            this.emitProgress(doneProgress);
            return of(doneProgress);
          }),
        );
      }),
      catchError((error: any) => {
        if (this.cancelled) return EMPTY;

        const latest = this.loadPersistedState(state.uploadId) ?? state;
        const pausedProgress: UploadProgress = {
          state: 'paused',
          fileName: latest.fileName,
          totalBytes: latest.fileSize,
          uploadedBytes: this.calculateUploadedBytes(latest.completedParts, latest.partSize, latest.fileSize),
          percentage: this.calculatePercentage(latest.completedParts.length, latest.parts.length),
          uploadId: latest.uploadId,
          error: this.paused ? undefined : error?.message || 'Resume failed',
        };
        this.emitProgress(pausedProgress);
        return of(pausedProgress);
      }),
    );
  }

  resetIfIdle(): void {
    const current = this.uploadProgress$.value;
    if (current === null) return;
    if (current.state === 'done' || current.state === 'error') {
      this.uploadProgress$.next(null);
    }
  }

  cancelUpload(): Observable<void> {
    this.updateState({ cancelled: true });
    this.abortActiveXhrs();

    const uploadId = this.currentUploadId;
    if (uploadId) {
      return this.abortUpload(uploadId).pipe(
        catchError(() => of(undefined as any)),
        tap(() => {
          this.removePersistedState(uploadId);
          this.updateState({ currentUploadId: null });
          this.uploadProgress$.next(null);
        }),
        switchMap(() => of(undefined as void)),
      );
    }

    this.uploadProgress$.next(null);
    return of(undefined as void);
  }

  private computeSha256(file: File): Promise<string> {
    return file.arrayBuffer().then((arrayBuffer) => {
      return crypto.subtle.digest('SHA-256', arrayBuffer).then((hashBuffer) => {
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      });
    });
  }

  private initiateUpload(body: {
    target: {
      shootAssetId: string;
    };
    file: {
      fileName: string;
      fileSize: number;
      contentType: string;
      sha256: string;
    };
    additionalAssetKey?: string;
  }): Observable<InitiateUploadResponse> {
    const url = `${environment.apiBase}/post-assets/upload/initiate`;
    return this.http.post<InitiateUploadResponse>(url, body);
  }

  private completeUpload(
    uploadId: string,
    parts: CompletedPart[],
    postByUserId?: string,
    postByUserName?: string,
  ): Observable<any> {
    const url = `${environment.apiBase}/post-assets/upload/complete`;
    const sortedParts = [...parts].sort((a, b) => a.partNumber - b.partNumber);
    return this.http.post(url, { uploadId, parts: sortedParts, postByUserId, postByUserName });
  }

  private abortUpload(uploadId: string): Observable<any> {
    const url = `${environment.apiBase}/post-assets/upload/abort`;
    return this.http.post(url, { uploadId });
  }

  private uploadPartsObservable(
    file: File,
    response: InitiateUploadResponse,
    initialUploadedBytes: number = 0,
  ): Observable<CompletedPart[]> {
    if (response.parts.length === 0) {
      return of([]);
    }

    const partBytesUploaded = new Map<number, number>();

    return from(response.parts).pipe(
      mergeMap((part) => {
        if (this.paused || this.cancelled) return EMPTY;

        const start = (part.partNumber - 1) * response.partSize;
        const end = Math.min(start + response.partSize, file.size);
        const blob = file.slice(start, end);

        const onProgress = (loaded: number): void => {
          partBytesUploaded.set(part.partNumber, loaded);
          const newBytes = Array.from(partBytesUploaded.values()).reduce(
            (sum, v) => sum + v,
            0,
          );
          const totalUploaded = Math.min(initialUploadedBytes + newBytes, file.size);
          this.emitProgress({
            state: 'uploading',
            fileName: file.name,
            totalBytes: file.size,
            uploadedBytes: totalUploaded,
            percentage: this.calculatePercentage(totalUploaded, file.size),
            uploadId: response.uploadId,
          });
        };

        return this.uploadSinglePartWithRetry(part.presignedUrl, blob, part.partNumber, onProgress).pipe(
          tap((eTag) => {
            const completed: CompletedPart = { partNumber: part.partNumber, eTag };
            const persistedState = this.loadPersistedState(response.uploadId);
            if (persistedState) {
              this.persistState({
                ...persistedState,
                completedParts: [...persistedState.completedParts, completed],
              });
            }
          }),
          switchMap((eTag) => of({ partNumber: part.partNumber, eTag })),
        );
      }, 3),
      reduce<CompletedPart, CompletedPart[]>((acc, completed) => [...acc, completed], []),
    );
  }

  private uploadSinglePartWithRetry(
    presignedUrl: string,
    blob: Blob,
    partNumber: number,
    onProgress: (loaded: number) => void,
    maxRetries = 3,
  ): Observable<string> {
    return defer(() => {
      if (this.paused || this.cancelled) {
        return throwError(() => new Error('Part upload aborted'));
      }
      return this.uploadSinglePart(presignedUrl, blob, partNumber, onProgress);
    }).pipe(
      retry({
        count: maxRetries,
        delay: (error, retryCount) => {
          if (error.message === 'Part upload aborted') {
            return throwError(() => error);
          }
          const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 8000);
          return timer(delay);
        },
      }),
    );
  }

  private uploadSinglePart(
    presignedUrl: string,
    blob: Blob,
    _partNumber: number,
    onProgress: (loaded: number) => void,
  ): Observable<string> {
    return new Observable<string>((subscriber) => {
      const xhr = new XMLHttpRequest();
      this.updateState({
        activeXhrs: [...this.state$.value.activeXhrs, xhr],
      });

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded);
        }
      };

      xhr.onload = () => {
        this.removeXhr(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          const eTag = xhr.getResponseHeader('ETag');
          if (!eTag) {
            subscriber.error(new Error('Missing ETag in S3 response'));
            return;
          }
          subscriber.next(eTag);
          subscriber.complete();
        } else if (xhr.status === 503 || xhr.status === 500) {
          subscriber.error(new Error(`S3 returned ${xhr.status}, retryable`));
        } else {
          subscriber.error(new Error(`Part upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        this.removeXhr(xhr);
        subscriber.error(new Error('Part upload network error'));
      };

      xhr.onabort = () => {
        this.removeXhr(xhr);
        subscriber.error(new Error('Part upload aborted'));
      };

      xhr.open('PUT', presignedUrl);
      xhr.send(blob);

      return () => {
        try {
          xhr.abort();
        } catch {
          // Ignore abort errors on teardown
        }
      };
    });
  }

  private updateState(partial: Partial<ServiceState>): void {
    const current = this.state$.value;
    this.state$.next({ ...current, ...partial });
  }

  private abortActiveXhrs(): void {
    const xhrs = [...this.state$.value.activeXhrs];
    this.updateState({ activeXhrs: [] });
    xhrs.forEach((xhr) => {
      try {
        xhr.abort();
      } catch {
        // Ignore abort errors
      }
    });
  }

  private removeXhr(xhr: XMLHttpRequest): void {
    const current = this.state$.value.activeXhrs;
    this.updateState({
      activeXhrs: current.filter((x) => x !== xhr),
    });
  }

  private emitProgress(progress: UploadProgress): void {
    this.uploadProgress$.next(progress);
  }

  private calculateUploadedBytes(
    completedParts: CompletedPart[],
    partSize: number,
    totalSize: number,
  ): number {
    if (completedParts.length === 0) return 0;
    const totalParts = Math.ceil(totalSize / partSize);
    const fullParts = completedParts.filter(
      (p) => p.partNumber < totalParts,
    ).length;
    const hasLastPart = completedParts.some((p) => p.partNumber === totalParts);
    const lastPartSize = totalSize - (totalParts - 1) * partSize;
    return fullParts * partSize + (hasLastPart ? lastPartSize : 0);
  }

  private calculatePercentage(numerator: number, denominator: number): number {
    return !Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0
      ? 0
      : Math.min(Math.round((numerator / denominator) * 100), 100);
  }

  private storageKey(uploadId: string): string {
    return `post-asset-upload-${uploadId}`;
  }

  private persistState(state: PersistedUploadState): void {
    try {
      localStorage.setItem(this.storageKey(state.uploadId), JSON.stringify(state));
    } catch {
      // localStorage may be full or unavailable
    }
  }

  private loadPersistedState(uploadId: string): PersistedUploadState | null {
    try {
      const raw = localStorage.getItem(this.storageKey(uploadId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private removePersistedState(uploadId: string): void {
    try {
      localStorage.removeItem(this.storageKey(uploadId));
    } catch {
      // Ignore
    }
  }
}
